// DraggableRuleSetList.tsx
import { useEffect, useRef, useState } from "react";
import { ReactSortable, Sortable, Store } from "react-sortablejs";
import styled, { useTheme } from "styled-components";
import Icon from "../../../components/Icon/Icon";
import { sortableOptions } from "../../../components/Draggable/Draggable";
import { useAlert } from "../../../components/Alert/Alerts";
import { ThemisRuleSetListItemDto } from "./manageThemisRuleSets";

export const StyledRuleSetCard = styled.div`
  position: relative;
  display: flex;
  flex-direction: row;
  gap: 0.5rem;
  background: ${({ theme }) => theme.colorBackgroundLight};
  padding: 0.5rem;
  padding-left: calc(0.5rem + 2px);
  border: solid 1px ${({ theme }) => theme.colorBorderLight};
  cursor: pointer;

  .ruleset-display {
    display: flex;
    flex-direction: column;
    justify-content: center;
    min-height: 24px;
    margin-right: auto;
  }

  .ruleset-name {
    font-weight: 600;
  }

  .ruleset-key {
    font-size: 0.85rem;
    opacity: 0.7;
  }
`;

type Props = {
  ruleSets: ThemisRuleSetListItemDto[];
  onEdit(id: number): void;
  onDelete(id: number): void;
  onReorder(orderedRuleSetIds: number[]): Promise<void>;
};

export default function DraggableRuleSetList({
  ruleSets,
  onEdit,
  onDelete,
  onReorder,
}: Props) {
  const { addNewAlert } = useAlert();
  const ref = useRef<ThemisRuleSetListItemDto[] | null>(null);
  const [list, setList] = useState<ThemisRuleSetListItemDto[]>([]);
  const [isSavingOrder, setIsSavingOrder] = useState(false);

  useEffect(() => {
    ref.current = ruleSets;
    setList(ruleSets);
  }, [ruleSets]);

  const handleReorder = async (
    newOrder: ThemisRuleSetListItemDto[],
    _sortable: Sortable | null,
    store: Store,
  ) => {
    if (!store.dragging) return;

    const prev = ref.current ?? [];
    if (prev.length !== newOrder.length) return;

    if (
      JSON.stringify(prev.map((x) => x.id)) ===
      JSON.stringify(newOrder.map((x) => x.id))
    ) {
      ref.current = newOrder;
      return;
    }

    setList(newOrder);
    ref.current = newOrder;

    if (isSavingOrder) return;

    setIsSavingOrder(true);
    try {
      await onReorder(newOrder.map((x) => x.id));
      addNewAlert({ type: "success", message: "Successfully updated order." });
    } catch {
      addNewAlert({ type: "error", message: "Failed to update order." });
    } finally {
      setIsSavingOrder(false);
    }
  };

  return (
    <ReactSortable
      list={list}
      setList={(newList, sortable, store) => {
        void handleReorder(newList, sortable, store);
      }}
      {...sortableOptions({
        group: "themis-rulesets",
        name: "themis-rulesets",
        drop: false,
        sortable: true,
      })}
    >
      {list.length > 0 ? (
        list.map((rs) => (
          <RuleSetCard
            key={rs.id}
            ruleSet={rs}
            onEdit={onEdit}
            onDelete={onDelete}
          />
        ))
      ) : (
        <p>No rule sets found</p>
      )}
    </ReactSortable>
  );
}

function RuleSetCard({
  ruleSet,
  onEdit,
  onDelete,
}: {
  ruleSet: ThemisRuleSetListItemDto;
  onEdit(id: number): void;
  onDelete(id: number): void;
}) {
  const theme = useTheme();

  return (
    <StyledRuleSetCard className="draggable">
      <div className="ruleset-display">
        <p className="ruleset-name">{ruleSet.name}</p>
        <p className="ruleset-key">{ruleSet.key}</p>
      </div>

      <span
        className="self-center"
        onClick={(e) => {
          e.stopPropagation();
          onEdit(ruleSet.id);
        }}
      >
        <Icon
          icon="edit"
          color={theme.colorPrimary}
          width="18px"
          height="18px"
        />
      </span>

      <span
        className="self-center"
        onClick={(e) => {
          e.stopPropagation();
          onDelete(ruleSet.id);
        }}
      >
        <Icon
          icon="trash"
          color={theme.colorPrimary}
          width="18px"
          height="18px"
        />
      </span>
    </StyledRuleSetCard>
  );
}
